import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@common/services/toast.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, interval, take } from 'rxjs';

import { PasswordRecoveryContext, PasswordRecoveryEvent, PasswordRecoveryMachine, PasswordRecoveryState, RecoveryTenantOption } from '../interfaces';

const COUNTDOWN_TIME = 180;

const DEFAULT_SEND_FAIL_TITLE = 'No se ha podido enviar el correo';
const DEFAULT_SEND_FAIL_MESSAGE = 'Intenta de nuevo en unos minutos';
const CODE_SENT_MESSAGE = 'Si el correo está registrado, recibirás un código para restablecer la contraseña';
const NO_RECOVERY_ACCOUNTS_MESSAGE = 'No se pudo completar la recuperación. Solicita un nuevo código.';

@Injectable({
    providedIn: 'root',
})
export class PasswordRecoveryService {
    passwordRecoveryMachine: PasswordRecoveryMachine = {
        context: {
            code: null,
            email: null,
            tenantId: null,
            tenants: null,
        },
        initialState: 'request_mail',
        states: {
            request_mail: {
                on: {
                    SUBMIT_MAIL: {
                        target: 'creating_code',
                        action: ({ email }: any) => {
                            this.updateContext({ key: 'email', value: email });
                            this.updateContext({ key: 'tenantId', value: null });
                            this.updateContext({ key: 'tenants', value: null });
                        },
                    },
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                        action: () => this.navigateToLogin(),
                    },
                    GO_TO_SIGNUP: {
                        target: 'request_mail',
                        action: () => this.navigateToSignup(),
                    },
                },
                entry: () => this.ensureForgotPasswordRoute(),
            },
            creating_code: {
                on: {
                    CODE_CREATE_OK: {
                        target: 'request_code',
                        action: () => {
                            this.toastService.success('Revise su correo', CODE_SENT_MESSAGE);
                        },
                    },
                    CODE_CREATE_FAIL: {
                        target: 'request_mail',
                        action: (payload?: { error?: any }) => {
                            this.handleSendCodeFailure(payload?.error);
                        },
                    },
                },
                entry: () => this.sendEmailWithCode(this.passwordRecoveryMachine.context.email),
            },
            request_code: {
                on: {
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                        action: () => this.navigateToLogin(),
                    },
                    SUBMIT_CODE: {
                        target: 'waiting_code_verification',
                        action: ({ code }) => {
                            this.updateContext({ key: 'code', value: code });
                        },
                    },
                    CODE_VERIFY_TIMEOUT: {
                        target: 'verification_code_error',
                        action: () => {
                            this.toastService.error('Error en código de verificación', 'El código de verificación ha expirado');
                        },
                    },
                },
                entry: () => this.verificationCountdown(COUNTDOWN_TIME),
            },
            waiting_code_verification: {
                on: {
                    CODE_VERIFY_OK: {
                        target: 'request_password',
                        action: () => {
                            this.toastService.success('Código validado', 'El código de recuperación ha sido verificado correctamente');
                        },
                    },
                    CODE_VERIFY_RETRY: {
                        target: 'request_code',
                        action: ({ attemptsRemaining }: { attemptsRemaining: number }) => {
                            this.updateContext({ key: 'code', value: '' });
                            const attemptsLabel = attemptsRemaining === 1 ? 'Te queda 1 intento' : `Te quedan ${attemptsRemaining} intentos`;
                            this.toastService.error('Código incorrecto', `${attemptsLabel}. Revisa el código e inténtalo de nuevo.`);
                        },
                    },
                    CODE_VERIFY_FAIL: {
                        target: 'verification_code_error',
                        action: (payload?: { message?: string }) => {
                            this.toastService.error('Error en código de verificación', payload?.message || 'Código de verificacíón no válido');
                        },
                    },
                },
                entry: () => this.verifyCode(this.passwordRecoveryMachine.context.code, this.passwordRecoveryMachine.context.email),
            },
            request_password: {
                on: {
                    SUBMIT_PASSWORD: {
                        target: 'updating_password',
                    },
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                        action: () => this.navigateToLogin(),
                    },
                },
            },
            updating_password: {
                on: {
                    PASSWORD_UPDATE_OK: {
                        target: 'password_success',
                        action: () => {
                            this.toastService.success('Operación exitosa', 'La contraseña ha sido actualizada correctamente');
                        },
                    },
                    PASSWORD_UPDATE_FAIL: {
                        target: 'password_error',
                        action: (payload?: { message?: string | null }) => {
                            this.toastService.error('Ha ocurrido un error', payload?.message || 'No se ha podido actualizar la contraseña');
                        },
                    },
                },
                entry: ({ password, confirmPassword, tenantId, applyToAllTenants }) =>
                    this.requestUpdatePassword(this.passwordRecoveryMachine.context.email, password, confirmPassword, tenantId ?? null, applyToAllTenants === true),
            },
            password_success: {
                on: {
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                        action: () => this.navigateToLogin(),
                    },
                },
            },
            password_error: {
                on: {
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                    },
                    CREATING_CODE: {
                        target: 'request_mail',
                    },
                    REQUEST_EMAIL: {
                        target: 'request_mail',
                        action: () => this.clearRecoveryContext(),
                    },
                },
            },
            verification_code_error: {
                on: {
                    GO_TO_LOGIN: {
                        target: 'request_mail',
                        action: () => this.navigateToLogin(),
                    },
                    REQUEST_EMAIL: {
                        target: 'request_mail',
                        action: () => this.clearRecoveryContext(),
                    },
                    RESEND_CODE: {
                        target: 'creating_code',
                        action: () => {
                            this.updateContext({ key: 'code', value: null });
                        },
                    },
                },
            },
        },
    };

    API_URL = environment.API_URL;
    counter$ = new BehaviorSubject<number>(COUNTDOWN_TIME);
    context$ = new BehaviorSubject<PasswordRecoveryContext>(this.passwordRecoveryMachine.context);
    ticks = interval(1000);
    ticksSubscription: any;
    passwordRecoveryState$ = new BehaviorSubject<PasswordRecoveryState>(this.passwordRecoveryMachine.initialState as PasswordRecoveryState);

    constructor(private http: HttpClient, private router: Router, private route: ActivatedRoute, private toastService: ToastService) {
        const currentState = this.passwordRecoveryState$.value;
        const stateEntryAction = this.passwordRecoveryMachine.states[currentState].entry;
        if (stateEntryAction) {
            stateEntryAction();
        }
    }

    send(event: PasswordRecoveryEvent, payload?: any) {
        const currentState = this.passwordRecoveryState$.value;

        const currentStateEvent = this.passwordRecoveryMachine.states[currentState].on[event];

        if (!currentStateEvent) {
            throw new Error(`Invalid state: [${currentState}][${event}]`);
        }

        const { action, target } = currentStateEvent;

        if (action) {
            action(payload);
        }

        this.passwordRecoveryState$.next(target as PasswordRecoveryState);
        this.context$.next(this.passwordRecoveryMachine.context);

        if (target !== currentState) {
            const stateEntryAction = this.passwordRecoveryMachine.states[target].entry;
            if (stateEntryAction) {
                stateEntryAction(payload);
            }
        }
    }

    /** Exposed for unit tests — maps API failures to a toast. */
    handleSendCodeFailure(error: any) {
        const body = error?.error ?? error ?? {};
        const title = typeof body.title === 'string' && body.title.trim() ? body.title : DEFAULT_SEND_FAIL_TITLE;
        const message = typeof body.message === 'string' && body.message.trim() ? body.message : DEFAULT_SEND_FAIL_MESSAGE;

        this.toastService.error(title, message);
    }

    private updateContext({ key, value }: { key: keyof PasswordRecoveryContext; value: any }) {
        if (!key) {
            throw new Error('Could not update context because key is missing');
        }
        const oldContext = { ...this.passwordRecoveryMachine.context };
        this.passwordRecoveryMachine.context = { ...oldContext, [key]: value };
    }

    private clearRecoveryContext() {
        this.updateContext({ key: 'code', value: null });
        this.updateContext({ key: 'email', value: null });
        this.updateContext({ key: 'tenantId', value: null });
        this.updateContext({ key: 'tenants', value: null });
        this.context$.next(this.passwordRecoveryMachine.context);
    }

    private ensureForgotPasswordRoute() {
        if (this.router.url !== '/auth/forgot-password') {
            this.router.navigate(['/auth/forgot-password']);
        }
    }

    private sendEmailWithCode(email: string) {
        this.http.post<any>(`${this.API_URL}/auth/send-recovery-email`, { email }).subscribe({
            next: () => {
                this.send('CODE_CREATE_OK', email);
            },
            error: error => {
                this.send('CODE_CREATE_FAIL', { error });
            },
        });
    }

    private navigateToLogin() {
        this.clearRecoveryContext();
        this.router.navigate(['/auth/login']);
    }

    private navigateToSignup() {
        this.clearRecoveryContext();
        this.router.navigate(['/auth/register']);
    }

    private verifyCode(code: string, email: string) {
        this.counter$.next(0);
        this.ticksSubscription.unsubscribe();

        this.http.post<any>(`${this.API_URL}/auth/verify-recovery-code`, { code, email }).subscribe({
            next: body => {
                // Los comercios del correo solo llegan con el código ya verificado.
                const tenants: RecoveryTenantOption[] = Array.isArray(body?.tenants) ? body.tenants : [];
                if (tenants.length === 0) {
                    this.send('CODE_VERIFY_FAIL', { message: NO_RECOVERY_ACCOUNTS_MESSAGE });
                    return;
                }
                const currentTenantId = tenants.some(t => t.tenantId === body?.currentTenantId) ? body.currentTenantId : null;
                this.updateContext({ key: 'tenants', value: tenants });
                this.updateContext({ key: 'tenantId', value: currentTenantId ?? (tenants.length === 1 ? tenants[0].tenantId : null) });
                this.send('CODE_VERIFY_OK', code);
            },
            error: error => {
                const body = error?.error ?? {};
                const attemptsRemaining = Number(body.attemptsRemaining);
                if (Number.isInteger(attemptsRemaining) && attemptsRemaining > 0) {
                    this.send('CODE_VERIFY_RETRY', { attemptsRemaining });
                    return;
                }
                this.send('CODE_VERIFY_FAIL', { message: typeof body.message === 'string' ? body.message : null });
            },
        });
    }

    private requestUpdatePassword(email: string, password: string, confirmPassword: string, tenantId: string | null, applyToAllTenants: boolean) {
        // El header `tenantid` le gana al tenant que el API infiere del Origin, así se
        // respeta el comercio elegido aunque la recuperación empiece en la URL de otro.
        const headers = !applyToAllTenants && tenantId ? new HttpHeaders({ tenantid: String(tenantId).trim().toLowerCase() }) : undefined;

        this.http.post<any>(`${this.API_URL}/auth/change-password-on-recovery`, { email, password, confirmPassword, applyToAllTenants }, { headers }).subscribe({
            next: () => {
                this.send('PASSWORD_UPDATE_OK', password);
            },
            error: error => {
                const message = error?.error?.message;
                this.send('PASSWORD_UPDATE_FAIL', { message: typeof message === 'string' ? message : null });
            },
        });
    }

    private verificationCountdown(counter: number) {
        this.ticksSubscription = this.ticks.pipe(take(COUNTDOWN_TIME)).subscribe(tick => {
            counter = counter - 1;

            this.counter$.next(counter);

            if (counter === 0) {
                this.send('CODE_VERIFY_TIMEOUT');
            }
        });
    }
}
